// src/pages/Devices.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  TableFooter,
  TableRow as MuiTableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";
import { request } from "../utils/request";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from '@mui/material/styles';

export default function Devices() {
  // table state
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // pagination
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  // search
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // ---- normalize single device ----
  const normalizeDevice = (d) => {
    if (!d) return null;
    const id = d && (d._id || d.id) ? String(d._id || d.id) : null;
    return {
      _id: id,
      userId: d.userId || d.user_id || d.user || "",
      osVersion: d.osVersion || d.os_version || "",
      ip: d.ip || d.clientIp || "",
      gps: d.gps || "",
      macAddress: d.macAddress || d.mac_address || "",
      wifiSSID: d.wifiSSID || d.wifi_ssid || "",
      userPrivilege: d.userPrivilege || d.user_privilege || "",
      firewall: d.firewall || "",
      antivirus: d.antivirus || "",
      defenderRealtime: d.defenderRealtime || d.defender_realtime || "",
      bitlocker: d.bitlocker || "",
      secureBoot: d.secureBoot || "",
      tpm: d.tpm || "",
      createdAt: d.createdAt || d.created_at || null,
      raw: d,
    };
  };

  // ---- helper: extract docs + total from many shapes ----
  const extractDocsAndTotal = (resp) => {
    let docs = [];
    let total = 0;

    if (!resp) return { docs: [], total: 0 };

    if (Array.isArray(resp)) {
      docs = resp;
      total = docs.length;
      return { docs, total };
    }

    if (resp.success && resp.data) {
      const d = resp.data;
      if (Array.isArray(d.docs)) {
        docs = d.docs;
        total = Number(d.total ?? d.meta?.total ?? docs.length) || docs.length;
        return { docs, total };
      }
      if (Array.isArray(d)) {
        docs = d;
        total = docs.length;
        return { docs, total };
      }
    }

    if (resp.data) {
      const d = resp.data;
      if (Array.isArray(d.docs)) {
        docs = d.docs;
        total = Number(d.total ?? d.meta?.total ?? docs.length) || docs.length;
        return { docs, total };
      }
      if (Array.isArray(d)) {
        docs = d;
        total = docs.length;
        return { docs, total };
      }
    }

    if (Array.isArray(resp.docs)) {
      docs = resp.docs;
      total = Number(resp.total ?? resp.meta?.total ?? docs.length) || docs.length;
      return { docs, total };
    }

    const firstArray = Object.values(resp).find((v) => Array.isArray(v));
    if (firstArray) {
      docs = firstArray;
      total = docs.length;
      return { docs, total };
    }

    return { docs: [], total: 0 };
  };

  // ---- REST fetch (paged) ----
  const fetchDevicesPage = useCallback(
    async (pageIndex = 0, limit = 25, search = "") => {
      setLoading(true);
      try {
        if (search) {
          try {
            const path = `devices?page=1&limit=1000&search=${encodeURIComponent(search)}`;
            const resp = await request(path, { method: "GET" });
            const { docs } = extractDocsAndTotal(resp);
            if (docs && docs.length > 0) {
              const normalized = docs.map(normalizeDevice).filter(Boolean);
              const t = search.toLowerCase();
              const filtered = normalized.filter((d) => {
                return (
                  (d.userId || "").toLowerCase().includes(t) ||
                  (d.ip || "").toLowerCase().includes(t) ||
                  (d.macAddress || "").toLowerCase().includes(t) ||
                  (d.osVersion || "").toLowerCase().includes(t)
                );
              });
              setDevices(filtered.slice(0, 1000));
              setTotalRows(filtered.length);
              setLastFetchedAt(new Date());
              return;
            }
          } catch (err) {
            console.debug("Server search failed, falling back to client-side", err?.message || err);
          }

          const fallbackResp = await request(`devices?page=1&limit=1000`, { method: "GET" });
          const { docs } = extractDocsAndTotal(fallbackResp);
          const normalized = docs.map(normalizeDevice).filter(Boolean);
          const t = search.toLowerCase();
          const filtered = normalized.filter((d) => {
            return (
              (d.userId || "").toLowerCase().includes(t) ||
              (d.ip || "").toLowerCase().includes(t) ||
              (d.macAddress || "").toLowerCase().includes(t) ||
              (d.osVersion || "").toLowerCase().includes(t)
            );
          });
          setDevices(filtered.slice(0, 1000));
          setTotalRows(filtered.length);
          setLastFetchedAt(new Date());
          return;
        }

        const backendPage = Math.max(1, pageIndex + 1);
        const resp = await request(`devices?page=${backendPage}&limit=${limit}`, { method: "GET" });
        const { docs, total } = extractDocsAndTotal(resp);
        const normalized = docs.map(normalizeDevice).filter(Boolean);
        setDevices(normalized);
        setTotalRows(Number(total || normalized.length || 0));
        setLastFetchedAt(new Date());
      } catch (err) {
        console.error("Devices: fetch failed", err);
        setDevices([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ---- IMPORTANT CHANGE ----
  // Previously this page auto-updated from WebSocket messages.
  // Per request: disable automatic live updates. The table will update only when:
  //  - the page mounts / params change (initial fetch)
  //  - the user clicks the Refresh button (calls refresh())
  //
  // (We intentionally do NOT attach any websocket handlers that mutate `devices`.)

  // ---- initial / param-driven fetch (REST fallback used mainly for paging) ----
  useEffect(() => {
    if (debouncedSearch) {
      setPage(0);
      fetchDevicesPage(0, rowsPerPage, debouncedSearch);
    } else {
      fetchDevicesPage(page, rowsPerPage, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  // ---- actions & UI helpers ----
  const refresh = async () => {
    // Always use REST fetch so refresh behavior is predictable and not automatic.
    setLoading(true);
    try {
      await fetchDevicesPage(page, rowsPerPage, debouncedSearch);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (ev, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (ev) => {
    const n = parseInt(ev.target.value, 10);
    setRowsPerPage(n);
    setPage(0);
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return format(d, "yyyy-MM-dd HH:mm:ss");
    } catch {
      return String(iso);
    }
  };

  const statusChip = (d) => {
    const insecure = ["disabled", "off", "absent", "no", ""].includes(String(d.bitlocker).toLowerCase()) ||
      ["disabled", "off", "absent", "no", ""].includes(String(d.firewall).toLowerCase()) ||
      ["disabled", "off", "absent", "no", ""].includes(String(d.antivirus).toLowerCase());
    return insecure ? (
      <Chip label="Insecure" size="small" sx={{ backgroundColor: "#fdecea", color: "#b71c1c" }} />
    ) : (
      <Chip label="Secure" size="small" sx={{ backgroundColor: "#e6f4ea", color: "#0b7a3d" }} />
    );
  };

  const effectiveTotal = useMemo(() => Number(totalRows || devices.length || 0), [totalRows, devices]);

  // ---------------------------
  // Selection & bulk-delete (NEW)
  // ---------------------------
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ----- ADDED: edit dialog state -----
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  // ------------------------------------

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = devices.length > 0 && selectedIds.length === devices.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(devices.map((d) => d._id));
    }
  };

  // ---- CHANGED: open edit dialog instead of alert ----
  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const id = selectedIds[0];
    const dev = devices.find((d) => d._id === id);
    // open dialog with normalized device fields
    setEditFormData({
      _id: dev?._id || id,
      userId: dev?.userId || "",
      osVersion: dev?.osVersion || "",
      ip: dev?.ip || "",
      macAddress: dev?.macAddress || "",
      wifiSSID: dev?.wifiSSID || "",
    });
    setEditDialogOpen(true);
    setSelectedIds([]); // clear selection (similar behaviour as other pages)
  };
  // ------------------------------------

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected device(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => request(`devices/${id}`, { method: "DELETE" }))
      );
      setSelectedIds([]);
      // refresh the current page
      await fetchDevicesPage(page, rowsPerPage, debouncedSearch);
      handleCancelBulkDelete();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      window.alert("Failed to delete selected devices.");
      setBulkDeleting(false);
    }
  };
  // ---------------------------

  // ----- ADDED: edit handlers -----
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData || !editFormData._id) return;
    setEditLoading(true);
    try {
      const id = editFormData._id;
      // send update to backend (using same pattern as delete endpoint)
      await request(`devices/${id}`, { method: "PUT", body: {
        userId: editFormData.userId,
        osVersion: editFormData.osVersion,
        ip: editFormData.ip,
        macAddress: editFormData.macAddress,
        wifiSSID: editFormData.wifiSSID,
      }});
      // refresh page data
      await fetchDevicesPage(page, rowsPerPage, debouncedSearch);
      setEditDialogOpen(false);
      setEditFormData(null);
      window.alert("Device updated successfully.");
    } catch (err) {
      console.error("Device update failed:", err);
      window.alert("Failed to update device.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditFormData(null);
    setEditLoading(false);
  };
  // --------------------------------

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        {/* replaced heading with breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <RouterLink
            to="/home"
            style={{
              textDecoration: "none",
              color: "#2d9cdb",
              fontSize: "0.95rem",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#0b1d51")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#2d9cdb")}
          >
            Dashboard
          </RouterLink>

          <Typography sx={{ fontSize: '0.95rem', color: 'black' }}>Devices</Typography>
        </Breadcrumbs>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: "1 1 auto", justifyContent: "flex-end" }}>
          <TextField
            size="small"
            placeholder="Search by userId, ip, mac, os"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              '& input::placeholder': {
                color: 'black',
                fontWeight: 100,
                opacity: 1,
              },
            }}
          />

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* --- Toolbar shown when rows selected (just above the table) --- */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography variant="body2">{selectedIds.length} selected</Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={handleEditSelected}
            disabled={selectedIds.length !== 1}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteSelected}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' }, color: '#fff', textTransform: 'none' }}
          >
            Delete
          </Button>

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper elevation={2} sx={{ height: 'calc(100vh - 170px)' }}>
        <Box sx={{ height: '100%', overflow: 'auto', width: '100%' }}>
          <Box component="table" width="100%" sx={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            {/* Table head */}
            <Box component="thead">
              <Box component="tr">
                {/* New: checkbox header */}
                <Box
                  component="th"
                  p={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#f5f7fb",
                    color: isDark ? '#fff' : '#333',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'select all devices' }}
                  />
                </Box>

                {['#', '', 'OS', 'IP', 'MAC', 'Wi-Fi', 'Privilege', 'Posture', 'Created'].map((h, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={1}
                    textAlign={i === 1 ? 'left' : 'left'}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#f5f7fb",
                      color: isDark ? '#fff' : '#333',
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Table body */}
            <Box component="tbody">
              {loading ? (
                <Box component="tr">
                  <Box component="td" colSpan={10} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </Box>
                </Box>
              ) : devices.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={10} align="center" sx={{ py: 4 }}>
                    No devices found.
                  </Box>
                </Box>
              ) : (
                devices.map((d, idx) => (
                  <Box component="tr" key={d._id || `${d.macAddress}-${idx}`} hover>
                    {/* row checkbox */}
                    <Box component="td" p={1} width={48} textAlign="center">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(d._id)}
                        onChange={() => toggleSelect(d._id)}
                        inputProps={{ 'aria-label': `select device ${d._id || d.macAddress}` }}
                      />
                    </Box>

                    <Box component="td" p={1}>{page * rowsPerPage + idx + 1}</Box>
                    <Box component="td" p={1} sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" noWrap>{d.userId || '—'}</Typography>
                    </Box>
                    <Box component="td" p={1}>{d.osVersion || '—'}</Box>
                    <Box component="td" p={1}>{d.ip || '—'}</Box>
                    <Box component="td" p={1}>{d.macAddress || '—'}</Box>
                    <Box component="td" p={1}>{d.wifiSSID || '—'}</Box>
                    <Box component="td" p={1}>{d.userPrivilege || '—'}</Box>
                    <Box component="td" p={1}>{statusChip(d)}</Box>
                    <Box component="td" p={1}>{fmt(d.createdAt)}</Box>
                  </Box>
                ))
              )}
            </Box>

            {/* Optional TableFooter if you have pagination */}
            <TableFooter>
              <MuiTableRow
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 3,
                  backgroundColor: (theme) => theme.palette.background.paper,
                }}
              >
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  colSpan={10}
                  count={effectiveTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page"
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '52px',
                    },
                    '& .MuiTablePagination-selectLabel': {
                      marginBottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiTablePagination-input': {
                      marginTop: 0,
                      marginBottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiTablePagination-displayedRows': {
                      marginBottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiTablePagination-actions': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                />
              </MuiTableRow>
            </TableFooter>

          </Box>
        </Box>
      </Paper>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={handleCancelBulkDelete}>
        <DialogTitle>Delete Selected Devices</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the following {selectedIds.length} device(s)?</Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map((id) => {
              const dev = devices.find((d) => d._id === id);
              return (
                <Typography key={id} variant="body2">
                  • {dev ? (dev.userId || dev.macAddress || dev._id) : id}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: '#777' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkDelete} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleConfirmBulkDelete} disabled={bulkDeleting} sx={{ backgroundColor: '#e53935', color: '#fff', '&:hover': { backgroundColor: '#b71c1c' } }}>
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------
          ADDED: Edit Device Dialog
         ------------------------- */}
      <Dialog open={editDialogOpen} onClose={handleEditCancel} fullWidth maxWidth="sm">
        <form onSubmit={handleEditSubmit}>
          <DialogTitle>Edit Device</DialogTitle>
          <DialogContent dividers>
            {editFormData ? (
              <Stack spacing={2}>
                <TextField
                  label="User ID"
                  name="userId"
                  value={editFormData.userId}
                  onChange={handleEditChange}
                  fullWidth
                />
                <TextField
                  label="OS Version"
                  name="osVersion"
                  value={editFormData.osVersion}
                  onChange={handleEditChange}
                  fullWidth
                />
                <TextField
                  label="IP Address"
                  name="ip"
                  value={editFormData.ip}
                  onChange={handleEditChange}
                  fullWidth
                />
                <TextField
                  label="MAC Address"
                  name="macAddress"
                  value={editFormData.macAddress}
                  onChange={handleEditChange}
                  fullWidth
                />
                <TextField
                  label="Wi-Fi SSID"
                  name="wifiSSID"
                  value={editFormData.wifiSSID}
                  onChange={handleEditChange}
                  fullWidth
                />
              </Stack>
            ) : (
              <Typography>Loading…</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditCancel} disabled={editLoading}>Cancel</Button>
            <Button type="submit" disabled={editLoading} variant="contained" sx={{ backgroundColor: '#2d9cdb' }}>
              {editLoading ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* ------------------------- */}

    </Box>
  );
}
